import React, { useState, useEffect, useMemo } from 'react';
import { Button, Table, Space, Row, Form, Modal, Input, Checkbox, Popconfirm } from 'antd';
import { last, first } from 'lodash';
import { DeleteFilled } from '@ant-design/icons';

import PageLayout from '../../Layout';
import { addAccount, getAccounts, deleteAccount } from '../../Services';
import { message } from '../../Utils';
import Pagination from '../../Components/Pagination';

const Account = () => {
  const [isVisibleModal, setVisibleModal] = useState(false);
  const [accounts, setAccounts] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    getAllAccounts();
  }, []);

  const getAllAccounts = (options) => {
    getAccounts(options).then(docs => {
      setAccounts(docs);
    });
  }

  const data = useMemo(() => {
    if (!accounts) return [];
    return accounts.map(doc => ({ ...doc.data(), key: doc.id }));
  }, [accounts]);

  const handleChangePage = (action) => {
    if (action === 'next') {
      getAllAccounts({ lastDoc: last(accounts) });
    } else {
      getAllAccounts({ firstDoc: first(accounts) });
    }
  }

  const handleOpenAddModal = () => {
    setVisibleModal(true);
    form.setFieldsValue({ admin: false });
  }

  const handleCloseAddModal = () => {
    setVisibleModal(false);
    form.resetFields();
  }

  const handleAddAccount = () => {
    form
      .validateFields()
      .then(values => {
        addAccount(values.email, values.password, values.admin).then(() => {
          handleCloseAddModal();
          message.success('Account is added.');
          getAllAccounts();
        }).catch(err => {
          if (err.code === "auth/email-already-in-use") {
            message.error('This email is already in use.');
          } else {
            message.error(err.message);
          }
        });
      });
  }

  const onClickDelete = (item) => {
    deleteAccount(item.key).then(() => {
      getAllAccounts();
      message.success('Item is deleted.');
    }).catch(() => {
      message.error('Something went wrong.');
    });
  }

  const columns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Admin',
      dataIndex: 'admin',
      key: 'admin',
      render: (_, record) => (<Checkbox checked={record.admin} />)
    },
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <Space size="middle">
          <Popconfirm
            placement="topRight"
            title="Are you sure to delete this item?"
            onConfirm={() => onClickDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" icon={<DeleteFilled />}>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <PageLayout title="Accounts">
      <Row justify='end' align="middle" style={{ marginTop: 10, marginBottom: 10 }}>
        <Button type="primary" onClick={handleOpenAddModal}>Add Account</Button>
      </Row>
      <Table columns={columns} dataSource={data} pagination={{ position: ["none", "none"] }} />
      <Pagination onChange={handleChangePage} size={data.length} />
      {isVisibleModal && (
        <Modal
          visible
          title={"Create a new account"}
          okText={"Create"}
          cancelText="Cancel"
          onCancel={handleCloseAddModal}
          onOk={handleAddAccount}
        >
          <Form
            form={form}
            layout="vertical"
            name="add_account"
          >
            <Form.Item
              name="email"
              label="Email"
              rules={[{ required: true, message: 'Please input email' }, { type: "email", message: 'Please input correct email' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="password"
              label="Password"
              rules={[{ required: true, message: 'Please input password' }]}
            >
              <Input.Password />
            </Form.Item>
            <Form.Item
              name="admin"
              valuePropName='checked'
            >
              <Checkbox>
                Admin
              </Checkbox>
            </Form.Item>
          </Form>
        </Modal>
      )}
    </PageLayout>
  );
}

export default Account;